/**
 * 2D矩阵变换工具类
 * 提供2D平面图形变换相关的工具函数和类
 */

import { Vec2d, VecLike } from './Vec2d';
import { EASINGS } from './easings';

export interface Transform2D {
  translateX: number;
  translateY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  skewX: number;
  skewY: number;
}

/**
 * 2D矩阵类
 * 表示3x3变换矩阵 [a, b, c, d, e, f, g, h, i]
 * 其中 g=h=0, i=1 用于2D变换
 */
export class Matrix2D {
  private matrix: number[];

  constructor(
    a: number = 1, b: number = 0, c: number = 0,
    d: number = 0, e: number = 1, f: number = 0,
    g: number = 0, h: number = 0, i: number = 1,
  ) {
    this.matrix = [a, b, c, d, e, f, g, h, i];
  }

  /**
   * 获取矩阵元素
   */
  getElement(row: number, col: number): number {
    return this.matrix[row * 3 + col];
  }

  /**
   * 设置矩阵元素
   */
  setElement(row: number, col: number, value: number): void {
    this.matrix[row * 3 + col] = value;
  }

  /**
   * 获取矩阵数组
   */
  toArray(): number[] {
    return [...this.matrix];
  }

  /**
   * 获取CSS transform字符串
   */
  toCSSTransform(): string {
    const [a, b, c, d, e, f] = this.matrix;
    // CSS matrix(a, b, c, d, tx, ty) 参数顺序：
    // a: scaleX, b: skewY, c: skewX, d: scaleY, tx: translateX, ty: translateY
    // 当前矩阵存储: [a, b, c, d, e, f] 对应 [scaleX, skewY, translateX, skewX, scaleY, translateY]
    // 正确映射: a->a(scaleX), b->b(skewY), c->d(skewX), d->e(scaleY), e->c(tx), f->f(ty)
    return `matrix(${a}, ${b}, ${d}, ${e}, ${c}, ${f})`;
  }

  /**
   * 矩阵乘法
   */
  multiply(other: Matrix2D): Matrix2D {
    const result = new Matrix2D();
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        let sum = 0;
        for (let k = 0; k < 3; k++) {
          sum += this.getElement(i, k) * other.getElement(k, j);
        }
        result.setElement(i, j, sum);
      }
    }
    return result;
  }

  /**
   * 矩阵求逆
   */
  inverse(): Matrix2D {
    const det = this.determinant();
    if (Math.abs(det) < 1e-10) {
      throw new Error('Matrix is not invertible');
    }

    const [a, b, c, d, e, f, g, h, i] = this.matrix;
    const invDet = 1 / det;

    return new Matrix2D(
      (e * i - f * h) * invDet, (c * h - b * i) * invDet, (b * f - c * e) * invDet,
      (f * g - d * i) * invDet, (a * i - c * g) * invDet, (c * d - a * f) * invDet,
      (d * h - e * g) * invDet, (b * g - a * h) * invDet, (a * e - b * d) * invDet,
    );
  }

  /**
   * 计算行列式
   */
  determinant(): number {
    const [a, b, c, d, e, f, g, h, i] = this.matrix;
    return a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  }

  /**
   * 克隆矩阵
   */
  clone(): Matrix2D {
    return new Matrix2D(...this.matrix);
  }

  /**
   * 判断是否为单位矩阵
   */
  isIdentity(): boolean {
    const identity = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    return this.matrix.every((val, index) => Math.abs(val - identity[index]) < 1e-10);
  }
}

/**
 * 创建单位矩阵
 */
export function createIdentityMatrix(): Matrix2D {
  return new Matrix2D();
}

/**
 * 创建平移矩阵
 */
export function createTranslationMatrix(tx: number, ty: number): Matrix2D {
  return new Matrix2D(1, 0, tx, 0, 1, ty, 0, 0, 1);
}

/**
 * 创建缩放矩阵
 */
export function createScaleMatrix(sx: number, sy: number = sx): Matrix2D {
  return new Matrix2D(sx, 0, 0, 0, sy, 0, 0, 0, 1);
}

/**
 * 创建旋转矩阵
 */
export function createRotationMatrix(angle: number): Matrix2D {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return new Matrix2D(cos, -sin, 0, sin, cos, 0, 0, 0, 1);
}

/**
 * 创建倾斜矩阵
 */
export function createSkewMatrix(skewX: number, skewY: number): Matrix2D {
  return new Matrix2D(1, Math.tan(skewX), 0, Math.tan(skewY), 1, 0, 0, 0, 1);
}

/**
 * 应用变换到点.这个看着有错误
 */
export function transformPoint(matrix: Matrix2D, point: VecLike): Vec2d {
  const [a, b, c, d, e, f] = matrix.toArray();
  return new Vec2d(
    a * point.x + b * point.y + c,
    d * point.x + e * point.y + f,
  );
}

/**
 * 应用变换到向量
 */
export function transformVector(matrix: Matrix2D, vector: VecLike): Vec2d {
  const [a, b, d, e] = matrix.toArray();
  return new Vec2d(
    a * vector.x + b * vector.y,
    d * vector.x + e * vector.y,
  );
}

/**
 * 逆向变换向量
 * 已知变换后的向量和变换矩阵，求原始向量
 * @param matrix 变换矩阵
 * @param transformedVector 变换后的向量
 * @returns 原始向量
 */
export function inverseTransformVector(matrix: Matrix2D, transformedVector: VecLike): Vec2d {
  const [a, b, d, e] = matrix.toArray();
  
  // 计算矩阵的行列式
  const det = a * e - b * d;
  
  if (Math.abs(det) < 1e-10) {
    throw new Error('Matrix is not invertible (determinant is zero)');
  }
  
  // 计算逆矩阵的2x2部分
  const invDet = 1 / det;
  const invA = e * invDet;
  const invB = -b * invDet;
  const invD = -d * invDet;
  const invE = a * invDet;
  
  // 应用逆变换
  return new Vec2d(
    invA * transformedVector.x + invB * transformedVector.y,
    invD * transformedVector.x + invE * transformedVector.y,
  );
}

/**
 * 应用变换到点数组
 */
export function transformPoints(matrix: Matrix2D, points: VecLike[]): Vec2d[] {
  return points.map(point => transformPoint(matrix, point));
}

/**
 * 组合多个变换矩阵
 */
export function composeTransforms(...matrices: Matrix2D[]): Matrix2D {
  return matrices.reduce((result, matrix) => result.multiply(matrix));
}

/**
 * 从变换参数创建矩阵
 */
export function createTransformMatrix(transform: Transform2D): Matrix2D {
  const translation = createTranslationMatrix(transform.translateX, transform.translateY);
  const scale = createScaleMatrix(transform.scaleX, transform.scaleY);
  const rotation = createRotationMatrix(transform.rotation);
  const skew = createSkewMatrix(transform.skewX, transform.skewY);

  return composeTransforms(translation, scale, rotation, skew);
}

/**
 * 分解矩阵为变换参数
 */
export function decomposeMatrix(matrix: Matrix2D): Transform2D {
  const [a, b, c, d, e, f] = matrix.toArray();
  
  // 提取平移
  const translateX = c;
  const translateY = f;

  // 提取缩放和旋转
  const det = a * e - b * d;
  const scaleX = Math.sqrt(a * a + b * b);
  const scaleY = Math.sqrt(d * d + e * e) * Math.sign(det);
  
  // 提取旋转角度
  const rotation = Math.atan2(b, a);

  // 提取倾斜
  const skewX = Math.atan2(-b, e);
  const skewY = Math.atan2(d, a);

  return {
    translateX,
    translateY,
    scaleX,
    scaleY,
    rotation,
    skewX,
    skewY,
  };
}

/**
 * 从矩阵中获取旋转角度
 * @param matrix 矩阵
 * @returns 旋转角度(逆时针为正，顺时针为360-角度)
 */
export function getAngleByMatrix(matrix: Matrix2D): number {
  const transform = decomposeMatrix(matrix);
  const rotation = transform.rotation;
  // 将弧度转换为角度
  let angle = rotation * (180 / Math.PI);
  
  // 将角度转换为与 getAngleByPoints 一致的方向
  angle = -angle;
  
  // 确保角度为正数：逆时针为正，顺时针为360-角度
  if (angle < 0) {
    angle = 360 + angle;
  }
  
  return angle;
}

/**
 * 插值两个矩阵
 */
export function interpolateMatrix(matrix1: Matrix2D, matrix2: Matrix2D, t: number): Matrix2D {
  const transform1 = decomposeMatrix(matrix1);
  const transform2 = decomposeMatrix(matrix2);

  const interpolated: Transform2D = {
    translateX: transform1.translateX + (transform2.translateX - transform1.translateX) * t,
    translateY: transform1.translateY + (transform2.translateY - transform1.translateY) * t,
    scaleX: transform1.scaleX + (transform2.scaleX - transform1.scaleX) * t,
    scaleY: transform1.scaleY + (transform2.scaleY - transform1.scaleY) * t,
    rotation: transform1.rotation + (transform2.rotation - transform1.rotation) * t,
    skewX: transform1.skewX + (transform2.skewX - transform1.skewX) * t,
    skewY: transform1.skewY + (transform2.skewY - transform1.skewY) * t,
  };

  return createTransformMatrix(interpolated);
}

/**
 * 矩阵变换工具类
 */
export class MatrixTransformUtils {
  /**
   * 围绕指定点旋转
   */
  static rotateAroundPoint(angle: number, centerX: number, centerY: number): Matrix2D {
    const translateToOrigin = createTranslationMatrix(-centerX, -centerY);
    const rotate = createRotationMatrix(angle);
    const translateBack = createTranslationMatrix(centerX, centerY);
    
    return composeTransforms(translateBack, rotate, translateToOrigin);
  }

  /**
   * 围绕指定点缩放
   */
  static scaleAroundPoint(sx: number, sy: number, centerX: number, centerY: number): Matrix2D {
    const translateToOrigin = createTranslationMatrix(-centerX, -centerY);
    const scale = createScaleMatrix(sx, sy);
    const translateBack = createTranslationMatrix(centerX, centerY);
    
    return composeTransforms(translateBack, scale, translateToOrigin);
  }

  /**
   * 计算多边形的质心
   */
  static polygonCentroid(points: VecLike[]): Vec2d {
    if (points.length === 0) return new Vec2d(0, 0);
    if (points.length === 1) return new Vec2d(points[0].x, points[0].y);

    let area = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      const cross = points[i].x * points[j].y - points[j].x * points[i].y;
      area += cross;
      cx += (points[i].x + points[j].x) * cross;
      cy += (points[i].y + points[j].y) * cross;
    }

    area /= 2;
    const factor = 1 / (6 * area);

    return new Vec2d(cx * factor, cy * factor);
  }

  /**
   * 计算包围盒
   */
  static boundingBox(points: VecLike[]): { min: Vec2d; max: Vec2d } {
    if (points.length === 0) {
      return { min: new Vec2d(0, 0), max: new Vec2d(0, 0) };
    }

    let minX = points[0].x;
    let minY = points[0].y;
    let maxX = points[0].x;
    let maxY = points[0].y;

    for (const point of points) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }

    return {
      min: new Vec2d(minX, minY),
      max: new Vec2d(maxX, maxY),
    };
  }

  /**
   * 判断点是否在多边形内
   */
  static pointInPolygon(point: VecLike, polygon: VecLike[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
          (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * 计算两条线段的交点
   */
  static lineIntersection(
    line1Start: VecLike, line1End: VecLike,
    line2Start: VecLike, line2End: VecLike,
  ): Vec2d | null {
    const x1 = line1Start.x, y1 = line1Start.y;
    const x2 = line1End.x, y2 = line1End.y;
    const x3 = line2Start.x, y3 = line2Start.y;
    const x4 = line2End.x, y4 = line2End.y;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null; // 平行线

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return new Vec2d(
        x1 + t * (x2 - x1),
        y1 + t * (y2 - y1),
      );
    }

    return null;
  }
}

/**
 * 矩阵动画工具类
 */
export class MatrixAnimationUtils {
  /**
   * 创建动画帧
   */
  static createAnimationFrames(
    startMatrix: Matrix2D,
    endMatrix: Matrix2D,
    duration: number,
    easingFn: (t: number) => number = EASINGS.linear,
  ): Matrix2D[] {
    const frames: Matrix2D[] = [];
    const frameCount = Math.ceil(duration * 60); // 60fps

    for (let i = 0; i <= frameCount; i++) {
      const t = i / frameCount;
      const easedT = easingFn(t);
      frames.push(interpolateMatrix(startMatrix, endMatrix, easedT));
    }

    return frames;
  }

  /**
   * 使用缓动函数插值矩阵
   */
  static interpolateWithEasing(
    matrix1: Matrix2D,
    matrix2: Matrix2D,
    t: number,
    easingType: keyof typeof EASINGS = 'linear',
  ): Matrix2D {
    const easedT = EASINGS[easingType](t);
    return interpolateMatrix(matrix1, matrix2, easedT);
  }
}

// 导出默认的矩阵类
export default Matrix2D;
